/** Validação e montagem do payload de tarefa (UX); regras finais no backend/RLS. */
import type { Task } from '../../../types';
import { buildDateTimeIso } from '../../../components/ui/DateTimeField';
import { sanitizeMessageText } from '../../../utils/sanitize';

export const TASK_TITLE_MIN = 3;
export const TASK_TITLE_MAX = 200;
export const TASK_DESCRIPTION_MAX = 2000;

export type TaskFormFields = {
  title: string;
  description: string;
  dueDate: string;
  dueTime: string;
  isCompleted: boolean;
  leadId: string;
};

export type LeadOption = {
  value: string;
  label: string;
  /** Responsável da negociação (`opportunities.assigned_to`) */
  assignedToId?: string | null;
};

export type TaskFormPayloadResult =
  | { ok: true; payload: Partial<Task> }
  | { ok: false; message: string };

function clampText(value: string, max: number): string {
  return value.length > max ? value.slice(0, max) : value;
}

/** Aceita leadId só se estiver nas opções ou for o vínculo original (edição). */
export function resolveLeadId(
  leadId: string,
  options: LeadOption[],
  existingLeadId?: string | null,
): string | undefined {
  if (!leadId) return undefined;
  if (existingLeadId && leadId === existingLeadId) return leadId;
  if (options.some(o => o.value === leadId)) return leadId;
  return undefined;
}

/**
 * Responsável da tarefa:
 * - com negociação e responsável na negociação → usa esse responsável
 * - sem negociação (ou negociação sem responsável) → usa o criador (`fallback`)
 */
export function resolveTaskAssignee(
  leadId: string | undefined,
  leadOptions: LeadOption[],
  fallback?: Task['assignedTo'],
): Task['assignedTo'] {
  if (!leadId) return fallback;
  const lead = leadOptions.find(o => o.value === leadId);
  if (lead?.assignedToId) {
    return { id: lead.assignedToId, name: '' };
  }
  return fallback;
}

export function buildTaskFormPayload(
  fields: TaskFormFields,
  options: {
    /** Criador da tarefa — usado quando não há negociação/responsável nela */
    assignedTo?: Task['assignedTo'];
    leadOptions: LeadOption[];
    existingLeadId?: string | null;
  },
): TaskFormPayloadResult {
  const title = clampText(sanitizeMessageText(fields.title.trim()), TASK_TITLE_MAX);

  if (title.length < TASK_TITLE_MIN) {
    return { ok: false, message: `O título deve ter pelo menos ${TASK_TITLE_MIN} caracteres.` };
  }

  const descriptionRaw = fields.description.trim();
  const description = descriptionRaw
    ? clampText(sanitizeMessageText(descriptionRaw), TASK_DESCRIPTION_MAX)
    : undefined;

  const leadId = resolveLeadId(fields.leadId, options.leadOptions, options.existingLeadId);
  const assignedTo = resolveTaskAssignee(leadId, options.leadOptions, options.assignedTo);

  return {
    ok: true,
    payload: {
      title,
      description,
      dueDate: buildDateTimeIso(fields.dueDate, fields.dueTime),
      isCompleted: fields.isCompleted,
      leadId,
      assignedTo,
    },
  };
}
